import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { PencilSimple, Plus, Trash, ArrowUp, ArrowDown, Eye, FloppyDisk, ArrowClockwise, RocketLaunch, ClockCounterClockwise, ArrowUUpLeft } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";

const LANGS = [["de", "Deutsch"], ["en", "English"]];
const TABS = [
  ["faq", "FAQ"], ["support", "Support"], ["finder", "Scan-Seite"],
  ["onboarding", "Onboarding"], ["general", "Allgemein"], ["ai", "Safe Buddy AI"],
];
const CAT_KEYS = ["nfc_tag", "buddy", "qr", "login", "group", "activation", "shop", "premium", "other"];
const CANNED_META = [["received", "Eingang bestätigen"], ["need_info", "Mehr Infos anfragen"], ["fixed", "Als behoben melden"]];
const FINDER_META = [
  ["finderIntro", "Einleitungstext"], ["emergencyContacts", "Überschrift Kontakte"], ["callNow", "Button: Jetzt anrufen"],
  ["revealCall", "Button: Nummer anzeigen"], ["importantInfo", "Überschrift Hinweise"], ["leaveMessage", "Überschrift Nachricht"],
  ["msgFormHint", "Hinweis Nachrichtenformular"], ["sendMessage", "Button: Nachricht senden"], ["tagBlocked", "Text: gesperrt"],
  ["deactivatedNotice", "Text: deaktiviert"], ["notAvailable", "Text: nicht verfügbar"], ["poweredBy", "Fußzeile"],
];
const ONB_META = [
  ["onbTitle1", "Titel Schritt 1"], ["onbText1", "Text Schritt 1"], ["onbTitle2", "Titel Schritt 2"], ["onbText2", "Text Schritt 2"],
  ["onbTitle3", "Titel Schritt 3"], ["onbText3", "Text Schritt 3"], ["onbViewGuide", "Link: Anleitung ansehen"],
];
const GENERAL_META = [
  ["save", "Speichern"], ["saved", "Bestätigung: Gespeichert"], ["cancel", "Abbrechen"], ["confirm", "Bestätigen (verloren)"],
  ["skip", "Überspringen"], ["next", "Weiter"], ["getStarted", "Los geht's"], ["delete", "Löschen"],
];
const AI_META = [["greeting", "Begrüßungstext"], ["help", "Hilfetext"], ["error", "Fehlermeldung"]];

const Field = ({ label, value, onChange, area }) => (
  <label className="block font-body text-xs text-gray-600 mb-2">
    {label}
    {area
      ? <textarea className="sb-input mt-1" rows={2} value={value || ""} onChange={(e) => onChange(e.target.value)} />
      : <input className="sb-input mt-1" value={value || ""} onChange={(e) => onChange(e.target.value)} />}
  </label>
);

const fmt = (iso) => { try { return new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return iso || ""; } };

export function ContentAdmin() {
  const { loadContent } = useApp();
  const [lang, setLang] = useState("de");
  const [tab, setTab] = useState("faq");
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(false);
  const [histOpen, setHistOpen] = useState(false);
  const [versions, setVersions] = useState([]);

  const load = () => api.get("/admin/content").then((r) => setDraft(r.data.draft)).catch(() => toast.error("Laden fehlgeschlagen"));
  useEffect(() => { load(); }, []);
  if (!draft) return <div className="sb-card p-5" data-testid="admin-content"><p className="font-body text-sm text-gray-500">…</p></div>;

  const cur = draft[lang] || {};
  const upd = (fn) => setDraft((d) => { const n = JSON.parse(JSON.stringify(d)); fn(n[lang]); return n; });

  const save = async () => { setBusy(true); try { await api.put("/admin/content", { content: draft }); toast.success("Entwurf gespeichert"); } catch { toast.error("Fehler"); } finally { setBusy(false); } };
  const discard = async () => { setBusy(true); try { await api.post("/admin/content/discard"); await load(); toast.success("Änderungen verworfen"); } catch { toast.error("Fehler"); } finally { setBusy(false); } };
  const publish = async () => { setBusy(true); try { await api.put("/admin/content", { content: draft }); await api.post("/admin/content/publish"); loadContent && loadContent(); toast.success("Veröffentlicht – jetzt live"); } catch { toast.error("Fehler"); } finally { setBusy(false); } };

  const openHistory = async () => { try { const r = await api.get("/admin/content/versions"); setVersions(r.data); setHistOpen(true); } catch { toast.error("Fehler"); } };
  const restore = async (vid) => { setBusy(true); try { await api.post(`/admin/content/versions/${vid}/restore`); await load(); loadContent && loadContent(); setHistOpen(false); toast.success("Version wiederhergestellt & live"); } catch { toast.error("Fehler"); } finally { setBusy(false); } };

  const faq = cur.faq || [];
  const addFaq = () => upd((c) => { c.faq = [...(c.faq || []), { id: Date.now().toString(), q: "Neue Frage", a: "Antwort" }]; });
  const delFaq = (i) => upd((c) => { c.faq.splice(i, 1); });
  const moveFaq = (i, dir) => upd((c) => { const j = i + dir; if (j < 0 || j >= c.faq.length) return; [c.faq[i], c.faq[j]] = [c.faq[j], c.faq[i]]; });

  return (
    <div className="sb-card p-5" data-testid="admin-content">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h2 className="text-lg font-heading font-semibold mb-1 flex items-center gap-2"><PencilSimple size={22} weight="duotone" /> Inhalte &amp; Texte</h2>
          <p className="font-body text-xs text-gray-500 mb-3">Bearbeite App-Texte selbst. Speichern legt einen Entwurf an, „Veröffentlichen" schaltet ihn live.</p>
        </div>
        <button data-testid="content-history-btn" onClick={openHistory} className="sb-btn px-2.5 py-1.5 bg-white text-xs font-medium flex items-center gap-1 shrink-0"><ClockCounterClockwise size={16} />Verlauf</button>
      </div>

      <div className="flex gap-1.5 mb-3" data-testid="content-langs">
        {LANGS.map(([v, l]) => (
          <button key={v} data-testid={`content-lang-${v}`} onClick={() => setLang(v)}
            className={`px-3 py-1 rounded-full text-xs font-heading border-2 ${lang === v ? "text-white border-gray-800" : "bg-white text-gray-600 border-gray-300"}`}
            style={lang === v ? { background: "var(--app-strong)" } : {}}>{l}</button>
        ))}
      </div>

      <div className="flex flex-wrap gap-1.5 mb-4" data-testid="content-tabs">
        {TABS.map(([v, l]) => (
          <button key={v} data-testid={`content-tab-${v}`} onClick={() => setTab(v)}
            className={`px-2.5 py-1 rounded-full text-xs font-heading border-2 ${tab === v ? "text-white border-gray-800" : "bg-white text-gray-600 border-gray-300"}`}
            style={tab === v ? { background: "var(--app-primary)" } : {}}>{l}</button>
        ))}
      </div>

      {tab === "faq" && (
        <div className="space-y-3" data-testid="content-faq">
          {faq.map((f, i) => (
            <div key={f.id || i} data-testid={`content-faq-${i}`} className="border-2 border-gray-800 rounded-2xl p-3 bg-white">
              <div className="flex items-center justify-between mb-1">
                <span className="font-heading text-xs text-gray-500">#{i + 1}</span>
                <div className="flex gap-1">
                  <button data-testid={`faq-up-${i}`} onClick={() => moveFaq(i, -1)} className="p-1 rounded-lg border-2 border-gray-800 bg-white"><ArrowUp size={14} /></button>
                  <button data-testid={`faq-down-${i}`} onClick={() => moveFaq(i, 1)} className="p-1 rounded-lg border-2 border-gray-800 bg-white"><ArrowDown size={14} /></button>
                  <button data-testid={`faq-del-${i}`} onClick={() => delFaq(i)} className="p-1 rounded-lg border-2 border-gray-800 bg-white text-red-500"><Trash size={14} /></button>
                </div>
              </div>
              <Field label="Frage" value={f.q} onChange={(v) => upd((c) => { c.faq[i].q = v; })} />
              <Field label="Antwort" area value={f.a} onChange={(v) => upd((c) => { c.faq[i].a = v; })} />
            </div>
          ))}
          <button data-testid="faq-add" onClick={addFaq} className="sb-btn w-full py-2.5 bg-white font-medium flex items-center justify-center gap-1"><Plus size={16} />Frage hinzufügen</button>
        </div>
      )}

      {tab === "support" && (
        <div className="space-y-4" data-testid="content-support">
          <div>
            <p className="font-heading text-sm font-semibold mb-2">Kategorienamen</p>
            {CAT_KEYS.map((k) => <Field key={k} label={k} value={cur.support?.categories?.[k]} onChange={(v) => upd((c) => { c.support.categories[k] = v; })} />)}
          </div>
          <div>
            <p className="font-heading text-sm font-semibold mb-2">Hinweistexte je Kategorie (eine Zeile = ein Punkt)</p>
            {CAT_KEYS.map((k) => (
              <label key={k} className="block font-body text-xs text-gray-600 mb-2">{k}
                <textarea className="sb-input mt-1" rows={3} value={(cur.support?.troubleHints?.[k] || []).join("\n")} onChange={(e) => upd((c) => { c.support.troubleHints[k] = e.target.value.split("\n").filter((x) => x.trim()); })} />
              </label>
            ))}
          </div>
          <div>
            <p className="font-heading text-sm font-semibold mb-2">Vorgefertigte Admin-Antworten</p>
            {CANNED_META.map(([k, l]) => <Field key={k} label={l} area value={cur.support?.cannedReplies?.[k]} onChange={(v) => upd((c) => { c.support.cannedReplies[k] = v; })} />)}
          </div>
        </div>
      )}

      {tab === "finder" && (
        <div data-testid="content-finder">
          {FINDER_META.map(([k, l]) => <Field key={k} label={l} area={k === "finderIntro" || k === "msgFormHint"} value={cur.finder?.[k]} onChange={(v) => upd((c) => { c.finder[k] = v; })} />)}
        </div>
      )}

      {tab === "onboarding" && (
        <div data-testid="content-onboarding">
          {ONB_META.map(([k, l]) => <Field key={k} label={l} area={k.startsWith("onbText")} value={cur.i18n?.[k]} onChange={(v) => upd((c) => { c.i18n[k] = v; })} />)}
        </div>
      )}

      {tab === "general" && (
        <div data-testid="content-general">
          {GENERAL_META.map(([k, l]) => <Field key={k} label={l} value={cur.i18n?.[k]} onChange={(v) => upd((c) => { c.i18n[k] = v; })} />)}
        </div>
      )}

      {tab === "ai" && (
        <div data-testid="content-ai">
          <p className="font-body text-xs text-gray-500 mb-3 rounded-xl border-2 border-amber-400 bg-amber-50 px-3 py-2">Diese Texte werden verwendet, sobald die Safe-Buddy-AI-Funktion verfügbar ist.</p>
          {AI_META.map(([k, l]) => <Field key={k} label={l} area value={cur.ai?.[k]} onChange={(v) => upd((c) => { c.ai[k] = v; })} />)}
        </div>
      )}

      <div className="flex flex-wrap gap-2 mt-5 pt-4 border-t-2 border-gray-200">
        <button data-testid="content-preview" onClick={() => setPreview(true)} className="sb-btn px-3 py-2 bg-white text-sm font-medium flex items-center gap-1"><Eye size={16} />Vorschau</button>
        <button data-testid="content-save" onClick={save} disabled={busy} className="sb-btn px-3 py-2 bg-white text-sm font-medium flex items-center gap-1 disabled:opacity-50"><FloppyDisk size={16} />Entwurf speichern</button>
        <button data-testid="content-discard" onClick={discard} disabled={busy} className="sb-btn px-3 py-2 bg-white text-sm font-medium flex items-center gap-1 disabled:opacity-50"><ArrowClockwise size={16} />Verwerfen</button>
        <button data-testid="content-publish" onClick={publish} disabled={busy} className="sb-btn px-4 py-2 text-white text-sm font-medium flex items-center gap-1 disabled:opacity-50 ml-auto" style={{ background: "var(--app-primary)" }}><RocketLaunch size={16} />Veröffentlichen</button>
      </div>

      <Dialog open={preview} onOpenChange={setPreview}>
        <DialogContent data-testid="content-preview-dialog" className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Vorschau ({lang.toUpperCase()})</DialogTitle>
            <DialogDescription>So sehen die Texte nach dem Veröffentlichen aus.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="font-heading text-sm font-semibold mb-1">FAQ</p>
              <Accordion type="single" collapsible>
                {(cur.faq || []).map((f, i) => (
                  <AccordionItem key={i} value={`pv-${i}`}><AccordionTrigger className="text-left text-sm">{f.q}</AccordionTrigger><AccordionContent className="text-sm text-gray-600">{f.a}</AccordionContent></AccordionItem>
                ))}
              </Accordion>
            </div>
            <div className="rounded-2xl border-2 border-gray-800 p-3">
              <p className="font-heading text-sm font-semibold mb-1">Scan-Seite</p>
              <p className="font-body text-sm text-gray-700">{cur.finder?.finderIntro}</p>
              <p className="font-body text-xs text-gray-500 mt-1">{cur.finder?.msgFormHint}</p>
            </div>
            <div className="rounded-2xl border-2 border-gray-800 p-3">
              <p className="font-heading text-sm font-semibold mb-1">Onboarding</p>
              {[1, 2, 3].map((s) => (
                <div key={s} className="mb-1"><p className="font-heading text-sm">{cur.i18n?.[`onbTitle${s}`]}</p><p className="font-body text-xs text-gray-600">{cur.i18n?.[`onbText${s}`]}</p></div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={histOpen} onOpenChange={setHistOpen}>
        <DialogContent data-testid="content-history-dialog" className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><ClockCounterClockwise size={20} />Änderungshistorie</DialogTitle>
            <DialogDescription>Frühere veröffentlichte Versionen wiederherstellen.</DialogDescription>
          </DialogHeader>
          {versions.length === 0 && <p className="font-body text-sm text-gray-500" data-testid="history-empty">Noch keine veröffentlichten Versionen.</p>}
          <div className="space-y-2">
            {versions.map((v, i) => (
              <div key={v.id} data-testid={`version-row-${i}`} className="flex items-center justify-between gap-2 border-2 border-gray-800 rounded-2xl px-3 py-2 bg-white">
                <div className="min-w-0">
                  <p className="font-heading text-sm">{fmt(v.at)}{i === 0 ? " · aktuell" : ""}{v.restored ? " · wiederhergestellt" : ""}</p>
                  <p className="font-body text-xs text-gray-500 truncate">{v.by || "—"}</p>
                </div>
                {i !== 0 && <button data-testid={`version-restore-${i}`} onClick={() => restore(v.id)} disabled={busy} className="sb-btn px-3 py-1.5 text-white text-xs font-medium flex items-center gap-1 shrink-0 disabled:opacity-50" style={{ background: "var(--app-primary)" }}><ArrowUUpLeft size={14} />Wiederherstellen</button>}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
